import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  TextField,
  TablePagination
} from '@mui/material';
import { 
  Medication, 
  CheckCircle, 
  Visibility, 
  Person, 
  Event, 
  AccessTime, 
  Info,
  HistoryEdu,
  WaterDrop
} from '@mui/icons-material';
import api from '../../api/api';
import dayjs from 'dayjs';

const MedicacionCard = ({ hospitalizacionId }) => {
  // --- ESTADOS PRINCIPALES ---
  const [tratamientos, setTratamientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- ESTADOS PARA PAGINACIÓN ---
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // --- ESTADOS PARA GESTIÓN DE MODALES ---
  const [selectedTratamiento, setSelectedTratamiento] = useState(null);
  const [openDetalle, setOpenDetalle] = useState(false);
  const [openConfirmar, setOpenConfirmar] = useState(false);
  const [adminSeleccionada, setAdminSeleccionada] = useState(null);
  const [motivo, setMotivo] = useState('');

  // --- EFECTOS ---
  useEffect(() => {
    if (hospitalizacionId) {
      fetchTratamientos();
    }
  }, [hospitalizacionId]);

  // --- CARGA DE DATOS ---
  const fetchTratamientos = async () => {
    try {
      setLoading(true);
      setError('');
      // Obtiene tratamientos activos y sus administraciones programadas
      const res = await api.get(`/tratamientos/activos/${hospitalizacionId}`);
      setTratamientos(res.data);
    } catch (err) {
      console.error('Error cargando tratamientos:', err);
      setError('No se pudieron cargar los registros de medicación');
      setTratamientos([]);
    } finally {
      setLoading(false);
    }
  };

  // --- LÓGICA DE APLICACIÓN DE DOSIS ---
  const pedirConfirmacion = (admin) => {
    setAdminSeleccionada(admin);
    setOpenConfirmar(true);
  };

  const confirmarAplicacion = async () => {
    if (!adminSeleccionada) return;
    try {
      await api.put(`/administraciones/${adminSeleccionada.id_administracion}/aplicar`);
      setOpenConfirmar(false);
      setAdminSeleccionada(null);
      fetchTratamientos(); // Refrescar para ver el cambio de estado y la barra de progreso
    } catch (err) {
      console.error('Error marcando dosis:', err);
      alert('Error: No se pudo registrar la aplicación.');
    }
  };

  // --- GESTIÓN DE ESTADOS DEL TRATAMIENTO ---
  const handleVerDetalle = (t) => {
    setSelectedTratamiento(t);
    setMotivo(''); // Limpiar el motivo al abrir un nuevo detalle
    setOpenDetalle(true);
  };

  const obtenerDosisAPlicar = (t) => {
  if (t.proxima_dosis_pendiente) return t.proxima_dosis_pendiente;

  // Si no hay administraciones (como en tus datos actuales), 
  // evitamos que devuelva undefined
  if (t.administraciones && t.administraciones.length > 0) {
    return t.administraciones.find(a => a.estado_admin === 'pendiente');
  }
  return null;
};

  const handleChangeEstado = async (idTratamiento, nuevoEstado) => {
    if (!motivo.trim() && (nuevoEstado === 'suspendido' || nuevoEstado === 'cancelado')) {
      alert('Por favor, ingrese un motivo para realizar esta acción.');
      return;
    }

    try {
      const payload = { 
        estado: nuevoEstado,
        motivo_suspension: motivo, 
        fecha_suspension: dayjs().format('YYYY-MM-DD HH:mm:ss') 
      };

      if (nuevoEstado === 'finalizado') {
        payload.fecha_fin = dayjs().toISOString();
      }

      await api.patch(`/tratamientos/${idTratamiento}/estado`, payload);
      
      setOpenDetalle(false);
      setMotivo('');
      fetchTratamientos(); 
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      alert('Error técnico al actualizar el estado del tratamiento');
    }
  };

  // --- CÁLCULOS DE CUMPLIMIENTO (KPIs) ---
  const calcularCumplimiento = () => {
    if (tratamientos.length === 0) return 0;
    let totalDosis = 0;
    let dosisAplicadas = 0;

    tratamientos.forEach(t => {
      if (t.administraciones) {
        t.administraciones.forEach(admin => {
          totalDosis++;
          if (admin.estado_admin === 'aplicada') dosisAplicadas++;
        });
      }
    });
    return totalDosis > 0 ? Math.round((dosisAplicadas / totalDosis) * 100) : 0;
  };

  const cumplimiento = calcularCumplimiento();

  // --- RENDER DE CARGA ---
  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', p: 4, textAlign: 'center' }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Sincronizando plan de medicación...
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        {/* CABECERA Y PROGRESO */}
        <Box sx={{ p: 2, bgcolor: '#1e40af', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Medication sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              PLAN DE MEDICACIÓN
            </Typography>
          </Box>

          <Chip
            label={`${cumplimiento}% Cumplimiento`}
            color={cumplimiento >= 80 ? 'success' : cumplimiento >= 50 ? 'warning' : 'error'}
            sx={{ fontWeight: 'bold', color: 'white' }}
          />
        </Box>

        <Box sx={{ p: 2 }}>
          <LinearProgress
            variant="determinate"
            value={cumplimiento}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                bgcolor: cumplimiento >= 80 ? '#22c55e' : cumplimiento >= 50 ? '#f59e0b' : '#ef4444'
              }
            }}
          />
        </Box>

        {/* TABLA DE TRATAMIENTOS */}
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell>FÁRMACO</TableCell>
                <TableCell>DOSIS / VÍA</TableCell>
                <TableCell>FRECUENCIA</TableCell>
                <TableCell align="center">ESTADO</TableCell>
                <TableCell align="center">HORARIOS</TableCell>
                <TableCell align="right">ACCIONES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tratamientos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((t) => (
                  <TableRow
                    key={t.id_tratamiento}
                    sx={{
                      bgcolor: t.estado === 'suspendido' ? '#fff1f2' : 'inherit',
                      transition: '0.2s',
                      '&:hover': { bgcolor: '#f1f5f9' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" sx={{ textDecoration: t.estado === 'suspendido' ? 'line-through' : 'none' }}>
                        {t.medicamento}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.principio_activo || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{t.dosis_valor} {t.dosis_unidad}</Typography>
                      <Typography variant="caption" sx={{ bgcolor: '#f1f5f9', px: 0.5, borderRadius: 0.5 }}>{t.via}</Typography>
                    </TableCell>

                    <TableCell>
                      {t.frecuencia_horas ? `Cada ${t.frecuencia_horas}h` : 'Única'}
                      {t.es_infusion_continua && (
                        <Box>
                          <Chip label="CRI" size="small" color="info" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                        </Box>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={t.estado.toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          bgcolor: t.estado === 'activo' ? '#dcfce7' : t.estado === 'suspendido' ? '#fee2e2' : '#f1f5f9',
                          color: t.estado === 'activo' ? '#166534' : t.estado === 'suspendido' ? '#991b1b' : '#475569'
                        }}
                      />
                    </TableCell>

                    {/* --- SECCIÓN DE HORARIOS ACTUALIZADA --- */}
<TableCell align="center">
  <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
    {t.administraciones && t.administraciones.length > 0 ? (
      t.administraciones.map((admin) => (
        <Tooltip key={admin.id_administracion} title={admin.estado_admin.toUpperCase()}>
          <Chip
            label={admin.fecha_hora_real 
              ? dayjs(admin.fecha_hora_real).format('hh:mm A') 
              : dayjs(admin.fecha_hora_planeada).format('hh:mm A')
            }
            size="small"
            variant={admin.estado_admin === 'aplicada' ? 'filled' : 'outlined'}
            color={admin.estado_admin === 'aplicada' ? 'success' : 'default'}
            sx={{ 
              fontSize: '0.65rem', 
              height: 20,
              fontWeight: admin.estado_admin === 'aplicada' ? 'bold' : 'normal',
              bgcolor: admin.estado_admin === 'aplicada' ? '' : '#f1f5f9' // Gris claro para pendientes
            }}
          />
        </Tooltip>
      ))
    ) : (
      <Typography variant="caption" color="text.disabled">Única / Sin programar</Typography>
    )}
  </Stack>
</TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Ver detalles y notas">
                          <IconButton size="small" onClick={() => handleVerDetalle(t)}>
                            <Visibility fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>

                        {t.estado === 'activo' && t.proxima_dosis_pendiente && (
                          <Tooltip title={`Aplicar dosis de las ${dayjs(t.proxima_dosis_pendiente.fecha_hora_planeada).format('hh:mm A')}`}>
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => pedirConfirmacion(t.proxima_dosis_pendiente)}
                              sx={{
                                bgcolor: '#dcfce7',
                                border: '1px solid #22c55e',
                                '&:hover': { bgcolor: '#bbf7d0' }
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

              {tratamientos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay registros de medicación
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={tratamientos.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
        />
      </Card>

      {/* MODAL DE DETALLES DEL TRATAMIENTO */}
      <Dialog
        open={openDetalle}
        onClose={() => setOpenDetalle(false)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Medication color="primary" />
            <Typography variant="h6" fontWeight="bold">Detalle de Prescripción</Typography>
          </Box>
          {selectedTratamiento?.es_infusion_continua && (
            <Chip
              icon={<WaterDrop sx={{ fontSize: '1rem !important' }} />}
              label="INFUSIÓN CONTINUA (CRI)"
              color="info"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </DialogTitle>

        <DialogContent dividers>
          {selectedTratamiento && (
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HistoryEdu color="action" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    INFORMACIÓN DEL FÁRMACO
                  </Typography>
                </Box>
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {selectedTratamiento.medicamento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Principio Activo: {selectedTratamiento.principio_activo || 'No especificado'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">DOSIS TOTAL</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTratamiento.dosis_valor} {selectedTratamiento.dosis_unidad}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">VÍA DE ADMINISTRACIÓN</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTratamiento.via}
                </Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {selectedTratamiento.es_infusion_continua && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="info.main" fontWeight="bold" display="block">RITMO DE INFUSIÓN</Typography>
                  <Typography variant="body1" fontWeight="bold" color="info.dark">
                    {selectedTratamiento.ritmo_infusion || '0'} ml/h
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Info fontSize="inherit" /> INDICACIÓN Y MOTIVO CLÍNICO
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#fdfcf0', borderLeft: '4px solid #eab308', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {selectedTratamiento.indicacion || 'Sin indicaciones adicionales registradas.'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Event fontSize="inherit" /> FECHA INICIO
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(selectedTratamiento.fecha_inicio).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTime fontSize="inherit" /> FRECUENCIA
                    </Typography>
                    <Typography variant="body2">
                      Cada {selectedTratamiento.frecuencia_horas} horas
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Person fontSize="inherit" /> PROFESIONAL RESPONSABLE
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 32, height: 32, bgcolor: '#3b82f6', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.75rem' }}>
                      {selectedTratamiento.usuario_nombre?.substring(0, 2).toUpperCase() || '??'}
                    </Box>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" display="block">{selectedTratamiento.usuario_nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">Prescrito: {dayjs(selectedTratamiento.created_at).format('DD/MM/YY')}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12}>
  <Divider sx={{ my: 2 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', letterSpacing: 1 }}>
      GESTIÓN DE ESTADO
    </Typography>
  </Divider>                
  
  {/* Mostrar información si está Finalizado o Suspendido */}
  {(selectedTratamiento.estado === 'finalizado' || selectedTratamiento.fecha_fin) && (
    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
      <Typography variant="caption" color="success.main" fontWeight="bold">TRATAMIENTO FINALIZADO EL:</Typography>
      <Typography variant="body2" fontWeight="bold">
        {dayjs(selectedTratamiento.fecha_fin).format('DD/MM/YYYY hh:mm A')}
      </Typography>
    </Box>
  )}

  {selectedTratamiento.estado === 'suspendido' && selectedTratamiento.motivo_suspension && (
    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff1f2', borderRadius: 1, border: '1px solid #fecaca' }}>
      <Typography variant="caption" color="error" fontWeight="bold">MOTIVO DE SUSPENSIÓN:</Typography>
      <Typography variant="body2">{selectedTratamiento.motivo_suspension}</Typography>
    </Box>
  )}

  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
    <TextField
      label="Notas de cambio de estado / Motivo"
      fullWidth
      multiline
      rows={2}
      variant="outlined"
      size="small"
      value={motivo}
      onChange={(e) => setMotivo(e.target.value)}
      placeholder="Escriba aquí si va a suspender o finalizar con observaciones..."
    />
    
    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
      {/* Botón Activar (Solo si no está activo) */}
      {selectedTratamiento.estado !== 'activo' && (
        <Button 
          variant="contained" color="success" size="small"
          onClick={() => handleChangeEstado(selectedTratamiento.id_tratamiento, 'activo')}
        >Reactivar</Button>
      )}
      
      {/* Botón Suspender */}
      {selectedTratamiento.estado === 'activo' && (
        <Button 
          variant="contained" color="warning" size="small"
          onClick={() => handleChangeEstado(selectedTratamiento.id_tratamiento, 'suspendido')}
        >Suspender</Button>
      )}
      
      {/* Botón FINALIZAR (El que faltaba) */}
      {selectedTratamiento.estado !== 'finalizado' && (
        <Button 
          variant="contained" 
          sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#1e293b' } }} 
          size="small"
          onClick={() => handleChangeEstado(selectedTratamiento.id_tratamiento, 'finalizado')}
        >Finalizar Plan</Button>
      )}

      {/* Botón Cancelar */}
      {selectedTratamiento.estado !== 'cancelado' && (
        <Button 
          variant="outlined" color="error" size="small"
          onClick={() => handleChangeEstado(selectedTratamiento.id_tratamiento, 'cancelado')}
        >Cancelar</Button>
      )}
    </Stack>
  </Box>
</Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                  HISTORIAL DE ADMINISTRACIONES
                </Typography>
                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1 }}>
  <Table size="small">
    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
      <TableRow>
        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>PROGRAMADA</TableCell>
        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>ESTADO</TableCell>
        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>APLICADA POR</TableCell>
        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>HORA REAL</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {selectedTratamiento.administraciones
        ?.slice() 
        .sort((a, b) => dayjs(a.fecha_hora_planeada).unix() - dayjs(b.fecha_hora_planeada).unix())
        .map((admin) => (
          <TableRow 
            key={admin.id_administracion}
            sx={{ bgcolor: admin.estado_admin === 'aplicada' ? '#f0fdf4' : 'inherit' }}
          >
            <TableCell sx={{ fontSize: '0.75rem' }}>
              {dayjs(admin.fecha_hora_planeada).format('hh:mm A')}
            </TableCell>
            <TableCell>
              <Chip 
                label={admin.estado_admin.toUpperCase()} 
                size="small" 
                color={admin.estado_admin === 'aplicada' ? 'success' : 'default'} 
                variant={admin.estado_admin === 'aplicada' ? 'filled' : 'outlined'}
                sx={{ 
                  fontSize: '0.6rem', 
                  height: 18, 
                  fontWeight: 'bold',
                  bgcolor: admin.estado_admin === 'aplicada' ? '' : '#f1f5f9'
                }}
              />
            </TableCell>
            <TableCell sx={{ fontSize: '0.75rem' }}>
              {admin.estado_admin === 'aplicada' ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {admin.usuario_nombre_admin || 'Sistema'}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled">Pendiente</Typography>
              )}
            </TableCell>
            <TableCell sx={{ fontSize: '0.75rem' }}>
              {admin.fecha_hora_real ? (
                <Typography variant="caption" fontWeight="bold" color="success.main">
                  {dayjs(admin.fecha_hora_real).format('hh:mm A')}
                </Typography>
              ) : '--:--'}
            </TableCell>
          </TableRow>
        ))}
    </TableBody>
  </Table>
</TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setOpenDetalle(false)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG DE CONFIRMACIÓN DE APLICACIÓN */}
      <Dialog open={openConfirmar} onClose={() => setOpenConfirmar(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime color="primary" /> Confirmar Aplicación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">¿Marcar dosis como <strong>APLICADA</strong>?</Typography>
          {adminSeleccionada && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
              <Typography variant="body2">Programada: {dayjs(adminSeleccionada.fecha_hora_planeada).format('hh:mm A')}</Typography>
              <Typography variant="body2">Real: {dayjs().format('hh:mm A')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenConfirmar(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmarAplicacion} variant="contained" color="success" startIcon={<CheckCircle />}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MedicacionCard;